import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { SEO } from "@/components/SEO";

const ABOUT_IMG = "/products/about/";

const HERO_IMG = `${ABOUT_IMG}731077_a59299e2f0584801916a487245889825_mountains~mv2.jpg`;

const STORY_PHOTOS = [
  {
    src: `${ABOUT_IMG}731077_4585e8f6bd69456ab070a868f09ed0bb~mv2.jpg`,
    place: "Sahara Desert, North Africa",
    caption: "Weeks from the nearest sealed road. This is where gear either proves itself, or fails.",
  },
  {
    src: `${ABOUT_IMG}731077_e25a5fd8ec994de39cb7239e13a37143~mv2.jpg`,
    place: "Aralkum Desert, Uzbekistan",
    caption: "Once the bottom of the Aral Sea — now a vast, cracked desert we camped and drove across.",
  },
  {
    src: `${ABOUT_IMG}731077_fe95ab7653a8452a9d56bf920aa695a6~mv2.jpg`,
    place: "Gobi Desert, Mongolia",
    caption: "Fully exposed camp near the Chinese border. No shelter, no backup, no room for gear that lets you down.",
  },
  {
    src: `${ABOUT_IMG}731077_b5ae36d2ab244b77bc078d77ff56b10b~mv2.jpg`,
    place: "Gobi Desert, Mongolia",
    caption: "A miniature oasis tucked between giant dunes, near the border with China.",
  },
  {
    src: `${ABOUT_IMG}731077_76466fae45b9435d8487becf0635d12f~mv2.jpg`,
    place: "Sahara Desert, North Africa",
    caption: "Some Saharan villages aren't even on the map. You find them, or you don't.",
  },
  {
    src: `${ABOUT_IMG}731077_08c4f661c0524b5ea2a5742b509cf0a0~mv2.jpg`,
    place: "Kyrgyzstan / China border",
    caption: "Countless miles of dirt track inside a military-controlled zone — special permission required.",
  },
  {
    src: `${ABOUT_IMG}731077_f2800193e6744f97b54fd144d70ac8af~mv2.jpg`,
    place: "Remote Tajikistan",
    caption: "It's not BP. It's not Mobil. In a remote corner of Tajikistan, this is what a petrol station looks like.",
  },
  {
    src: `${ABOUT_IMG}731077_710f07653fa5424f8d7f72acc899bfb5~mv2.jpg`,
    place: "Afghanistan border",
    caption: "Mostly safe — but know which valleys still carry landmines before you go. Overlanding abroad demands a far broader margin for error than touring at home.",
  },
  {
    src: `${ABOUT_IMG}731077_6ad51d06b3414e64a34127ba180dfaa7~mv2.jpg`,
    place: "South Island, New Zealand",
    caption: "And where you'll usually find us: home in New Zealand's South Island — planning the next one.",
  },
];

export default function AboutPage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const closeLightbox = () => setLightboxIndex(null);
  const showPrev = (e) => { e.stopPropagation(); setLightboxIndex((i) => (i - 1 + STORY_PHOTOS.length) % STORY_PHOTOS.length); };
  const showNext = (e) => { e.stopPropagation(); setLightboxIndex((i) => (i + 1) % STORY_PHOTOS.length); };

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowLeft") setLightboxIndex((i) => (i - 1 + STORY_PHOTOS.length) % STORY_PHOTOS.length);
      if (e.key === "ArrowRight") setLightboxIndex((i) => (i + 1) % STORY_PHOTOS.length);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxIndex]);

  return (
    <div data-testid="about-page" className="flex flex-col">
      <SEO
        path="/about"
        title="About XpeditioNZ — Real Expedition Experience"
        description="XpeditioNZ was built from real, remote 4WD expeditions across dozens of countries and multiple continents — not marketing. Genuine expedition-grade overland gear, sourced from South Africa."
        image={HERO_IMG}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[85svh] flex items-start overflow-hidden order-1">
        <motion.div className="absolute inset-0" style={{ y: heroY, scale: heroScale }} data-testid="about-hero-parallax">
          <img
            src={HERO_IMG}
            alt="XpeditioNZ team at the Tajikistan/Afghanistan border"
            data-testid="about-hero-image"
            className="w-full h-full object-cover"
            style={{ objectPosition: "25% 35%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 from-5% via-black/10 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/35 to-transparent" style={{ height: "30%", top: "auto" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent md:via-transparent" />
        </motion.div>
        <div className="relative z-10 container-x pt-32 pb-16 w-full">
          <p className="overline mb-6">— Our Story</p>
          <h1 className="font-display text-[10vw] md:text-[7vw] lg:text-[5.8vw] leading-[0.95] text-white hero-text-shadow max-w-[16ch]">
            BUILT FROM REAL EXPEDITIONS.<br />
            <span className="text-[var(--brand-primary)]">NOT MARKETING.</span>
          </h1>
          <p className="font-mono text-[13px] font-semibold tracking-[0.25em] uppercase text-[var(--brand-primary)] mt-8">
            Tajikistan / Afghanistan Border
          </p>
          <p className="text-base md:text-lg font-medium md:font-normal text-white/95 md:text-white/80 leading-relaxed max-w-2xl mt-3 [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
            Every photo on this page is ours — captured on our own past overland expeditions, not stock imagery.
          </p>
        </div>
      </section>

      {/* THE EXPERIENCE */}
      <section className="container-x pt-6 pb-24 md:py-32 order-3 md:order-2">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline mb-6">— Where This Started</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95] text-white">
              DOZENS OF COUNTRIES.<br />
              <span className="text-[var(--brand-primary)]">ONE STANDARD</span><br />
              FOR GEAR.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[var(--text-secondary)] text-lg leading-relaxed">
            <p>
              XpeditioNZ wasn't built from a spreadsheet of trending products. It was built from years of operating expeditions across some of the planet's most remote and demanding environments — <span className="text-white">dozens of countries, spanning multiple continents,</span> from the Sahara Desert in North Africa to the Gobi Desert in Mongolia, East Asia.
            </p>
            <p>
              When reliability stops being optional — when the nearest help is days away, not a phone call — <span className="text-white">your standard for equipment changes permanently.</span> You learn fast what works, what fails under real load, and what genuinely lasts.
            </p>
            <p>
              That standard is the only one we apply to everything we sell.
            </p>
          </div>
        </div>
      </section>

      {/* THE GAP */}
      <section className="container-x pb-24 md:pb-32 border-t border-[var(--border-default)] pt-24 md:pt-32 order-4 md:order-3">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline mb-6">— Why XpeditioNZ Exists</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95] text-white">
              NZ WAS MISSING<br />
              <span className="text-[var(--brand-primary)]">EXPEDITION-GRADE.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[var(--text-secondary)] text-lg leading-relaxed">
            <p>
              New Zealand has long been saturated with heavily marketed, mid-tier overland gear — generic imports, sometimes priced like premium equipment. We saw the gap clearly: <span className="text-white">what's commonly sold here isn't what exists globally at true expedition grade.</span>
            </p>
            <p>
              Roof tents are a good example. NZ's market is crowded with tents marketed as "locally designed" or "locally made" — look closely and, in our experience, <span className="text-white">most are the same generic rack-tent design, simply rebadged under different names.</span> It's an easy claim to make and a hard one for buyers to check. Step up to a global brand with a genuinely original design and the cost difference may be less than you expect — the performance difference isn't.
            </p>
            <p>
              So we closed the gap. We source globally proven equipment from <span className="text-white">South Africa — the world's benchmark for serious expedition and remote-travel gear.</span> Not trend-driven accessories. Equipment engineered for durability, reliability and long-term performance, that also happens to be more refined and better to live with.
            </p>
            <p className="text-white italic border-l-2 border-[var(--brand-primary)] pl-4">
              Better materials, better engineering, better design — selected from real-world global experience, not a marketing brief.
            </p>
          </div>
        </div>
      </section>

      {/* PHOTO STORY GRID */}
      <section className="border-t border-[var(--border-default)] order-2 md:order-4">
        <div className="container-x pt-10 pb-6 md:py-32">
          <div className="mb-14 max-w-2xl">
            <p className="overline mb-4">— From The Road</p>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95]">
              WHERE OUR STANDARDS<br />
              <span className="text-[var(--brand-primary)]">WERE FORGED.</span>
            </h2>
            <p className="text-[var(--text-secondary)] mt-6 leading-relaxed text-lg">
              Every image below is from our own collection — captured by us, on our own expeditions. No stock photography.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
            {STORY_PHOTOS.map((p, i) => (
              <figure key={i} data-testid={`about-photo-${i}`} className="bg-[var(--bg-secondary)] group">
                <button
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className="relative aspect-[4/3] overflow-hidden w-full cursor-zoom-in block"
                  data-testid={`about-photo-expand-${i}`}
                >
                  <img
                    src={p.src}
                    alt={p.place}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                  <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--brand-primary)] bg-black/60 backdrop-blur-sm px-3 py-1.5">
                    {p.place}
                  </span>
                </button>
                <figcaption className="p-6">
                  <p className="text-[var(--text-secondary)] leading-relaxed text-sm">{p.caption}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* PHOTO LIGHTBOX */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          onClick={closeLightbox}
          data-testid="about-lightbox-overlay"
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-5 right-5 md:top-8 md:right-8 text-white/80 hover:text-white transition-colors z-10"
            data-testid="about-lightbox-close"
          >
            <X size={28} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={showPrev}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors p-2 z-10"
            data-testid="about-lightbox-prev"
          >
            <ChevronLeft size={36} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={showNext}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors p-2 z-10"
            data-testid="about-lightbox-next"
          >
            <ChevronRight size={36} strokeWidth={1.5} />
          </button>
          <div className="max-w-5xl w-full max-h-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={STORY_PHOTOS[lightboxIndex].src}
              alt={STORY_PHOTOS[lightboxIndex].place}
              className="max-h-[75vh] w-auto object-contain"
              data-testid="about-lightbox-image"
            />
            <div className="mt-5 text-center max-w-2xl">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--brand-primary)] mb-2">
                {STORY_PHOTOS[lightboxIndex].place}
              </p>
              <p className="text-white/80 text-sm leading-relaxed">{STORY_PHOTOS[lightboxIndex].caption}</p>
            </div>
          </div>
        </div>
      )}

      {/* CLOSING CTA */}
      <section className="container-x py-24 md:py-32 border-t border-[var(--border-default)] order-5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] max-w-3xl">
              WE ONLY STOCK GEAR<br />
              <span className="text-[var(--brand-primary)]">WE'D TAKE OURSELVES.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <Link to="/roof-racks" data-testid="about-cta-roofracks" className="btn-primary">
              Explore K9 Roof Racks <ArrowUpRight size={14} />
            </Link>
            <a href="/#enquire" data-testid="about-cta-enquire" className="btn-secondary">
              Get In Touch
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
